/**
 * Household Hooks
 *
 * React Query hooks for household management (WP-HOUSEHOLD-3).
 * Provides data fetching and mutation hooks for households, members, and invitations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { householdsApi } from '@/services/households';
import type {
  HouseholdListResponse,
  MemberListResponse,
  InvitationListResponse,
  InvitationResponse,
  CodeInvitationResponse,
  AcceptInvitationResponse,
  InvitationPreview,
  LeaveResponse,
  MessageResponse,
  SetDefaultHouseholdResponse,
  UpdateHouseholdResponse,
} from '@/types/household';

// Query keys for cache management
export const householdKeys = {
  all: ['households'] as const,
  myHouseholds: () => [...householdKeys.all, 'my'] as const,
  members: (tenantId: string) => [...householdKeys.all, tenantId, 'members'] as const,
  invitations: (tenantId: string) => [...householdKeys.all, tenantId, 'invitations'] as const,
  invitationPreview: (token: string) => [...householdKeys.all, 'preview', token] as const,
};

/**
 * Hook to fetch all households the current user belongs to
 */
export function useMyHouseholds() {
  return useQuery<HouseholdListResponse>({
    queryKey: householdKeys.myHouseholds(),
    queryFn: () => householdsApi.getMyHouseholds(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to fetch members of a household
 * @param tenantId - The household/tenant ID
 */
export function useHouseholdMembers(tenantId: string) {
  return useQuery<MemberListResponse>({
    queryKey: householdKeys.members(tenantId),
    queryFn: () => householdsApi.getMembers(tenantId),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
  });
}

/**
 * Hook to fetch pending invitations for a household
 * @param tenantId - The household/tenant ID
 */
export function useHouseholdInvitations(tenantId: string) {
  return useQuery<InvitationListResponse>({
    queryKey: householdKeys.invitations(tenantId),
    queryFn: () => householdsApi.listInvitations(tenantId),
    enabled: !!tenantId,
    staleTime: 1 * 60 * 1000, // Consider data fresh for 1 minute
  });
}

/**
 * Hook to get invitation preview (unauthenticated)
 * @param token - The invitation token or code
 */
export function useInvitationPreview(token: string) {
  return useQuery<InvitationPreview>({
    queryKey: householdKeys.invitationPreview(token),
    queryFn: () => householdsApi.getInvitationPreview(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    retry: false, // Don't retry on 404
  });
}

/**
 * Hook to create an email invitation
 */
export function useCreateEmailInvitation() {
  const queryClient = useQueryClient();

  return useMutation<InvitationResponse, Error, { tenantId: string; email: string }>({
    mutationFn: ({ tenantId, email }) => householdsApi.createEmailInvitation(tenantId, email),
    onSuccess: (_data, { tenantId }) => {
      // Invalidate invitations list
      queryClient.invalidateQueries({ queryKey: householdKeys.invitations(tenantId) });
    },
  });
}

/**
 * Hook to create a code invitation
 */
export function useCreateCodeInvitation() {
  const queryClient = useQueryClient();

  return useMutation<CodeInvitationResponse, Error, string>({
    mutationFn: (tenantId) => householdsApi.createCodeInvitation(tenantId),
    onSuccess: (_data, tenantId) => {
      // Invalidate invitations list
      queryClient.invalidateQueries({ queryKey: householdKeys.invitations(tenantId) });
    },
  });
}

/**
 * Hook to revoke an invitation
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation<MessageResponse, Error, { tenantId: string; invitationId: string }>({
    mutationFn: ({ tenantId, invitationId }) =>
      householdsApi.revokeInvitation(tenantId, invitationId),
    onSuccess: (_data, { tenantId }) => {
      // Invalidate invitations list
      queryClient.invalidateQueries({ queryKey: householdKeys.invitations(tenantId) });
    },
  });
}

/**
 * Hook to accept an invitation
 * WP-BUG-35: Uses selective cache invalidation to prevent race condition
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation<AcceptInvitationResponse, Error, string>({
    mutationFn: (tokenOrCode) => householdsApi.acceptInvitation(tokenOrCode),
    onSuccess: (_data, tokenOrCode) => {
      // WP-BUG-35: Remove the specific preview from cache instead of invalidating all
      // This prevents the race condition where refetch returns isValid: false
      // and causes "Invitation Expired" to show after successful acceptance
      queryClient.removeQueries({
        queryKey: householdKeys.invitationPreview(tokenOrCode)
      });
      // Invalidate other household data (membership, list, etc.)
      queryClient.invalidateQueries({
        queryKey: householdKeys.myHouseholds()
      });
    },
  });
}

/**
 * Hook to remove a member from a household
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation<MessageResponse, Error, { tenantId: string; userId: string }>({
    mutationFn: ({ tenantId, userId }) => householdsApi.removeMember(tenantId, userId),
    onSuccess: (_data, { tenantId }) => {
      // Invalidate members list
      queryClient.invalidateQueries({ queryKey: householdKeys.members(tenantId) });
      // Also invalidate households list (member count changed)
      queryClient.invalidateQueries({ queryKey: householdKeys.myHouseholds() });
    },
  });
}

/**
 * Hook to leave a household
 */
export function useLeaveHousehold() {
  const queryClient = useQueryClient();

  return useMutation<LeaveResponse, Error, string>({
    mutationFn: (tenantId) => householdsApi.leaveHousehold(tenantId),
    onSuccess: () => {
      // Invalidate all household data since membership changed
      queryClient.invalidateQueries({ queryKey: householdKeys.all });
    },
  });
}

/**
 * Hook to transfer ownership
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation<MessageResponse, Error, { tenantId: string; newOwnerId: string }>({
    mutationFn: ({ tenantId, newOwnerId }) =>
      householdsApi.transferOwnership(tenantId, newOwnerId),
    onSuccess: (_data, { tenantId }) => {
      // Invalidate members list (roles changed)
      queryClient.invalidateQueries({ queryKey: householdKeys.members(tenantId) });
      // Also invalidate households list (user's role changed)
      queryClient.invalidateQueries({ queryKey: householdKeys.myHouseholds() });
    },
  });
}

/**
 * Hook to set default household
 * WP-HOUSEHOLD-2: Sets the user's preferred household for login
 */
export function useSetDefaultHousehold() {
  const queryClient = useQueryClient();

  return useMutation<SetDefaultHouseholdResponse, Error, string>({
    mutationFn: (tenantId) => householdsApi.setDefaultHousehold(tenantId),
    onSuccess: () => {
      // Invalidate households list to refresh default status
      queryClient.invalidateQueries({ queryKey: householdKeys.myHouseholds() });
    },
  });
}

/**
 * Hook to update household settings (rename)
 * WP-HOUSEHOLD-2: Only household owner can rename
 */
export function useUpdateHousehold() {
  const queryClient = useQueryClient();

  return useMutation<UpdateHouseholdResponse, Error, { tenantId: string; name: string }>({
    mutationFn: ({ name }) => householdsApi.updateHousehold(name),
    onSuccess: () => {
      // Invalidate all household data since name changed
      queryClient.invalidateQueries({ queryKey: householdKeys.all });
    },
  });
}
